import { makeEnum, makeOptions,
  setupClass,
  interpolateInMap,
} from '../Base.js'
import { InputComponent } from '../Common/InputComponent.js'
import { CalcContext } from '../Common/CalcContext.js'

import { kFloorCoverings } from '../MaterialData/FloorCoverings.js'
import { GroundSurfaceTempAmplitudes } from '../Data/GroundSurfaceTempAmplitudes.js'
import * as calc from './ResidentialCalculations.js'

import { Units } from '../Common/Units.js'
import { Quantity } from '../Common/Quantity.js'
import { ErrorCollector } from '../Common/ErrorCollector.js'

import {
  FieldType,
  Field,
  FieldGroup,
} from '../Common/Field.js'

import {
  Season,
  ManualOrAutomatic,
  ManualOrSelectFromList,
 } from './Common.js'
 import { TypesSet } from './TypesSet.js'

import { gApp, DebugOn } from '../Globals.js'


export let FloorType = makeEnum({
  SlabOnGrade: 'Slab on grade',
  SlabBelowGrade: 'Slab below grade (basement)',
  FloorAboveCrawlSpace: 'Floor above crawl space or unconditioned space',
  FloorAboveConditionedSpace: 'Floor above conditioned space',
  FloorRaisedOffGround: 'Floor raised off ground',
})

let OuterWallConstruction = makeEnum({
  EightInchBlockWall: '8" block wall, brick facing',
  FourInchBlockWall: '4" block wall, brick facing',
  MetalStudWall: 'Metal stud wall with stucco and dry wall',
  PouredConcreteWall: 'Poured concrete wall',
})

// Coefficients A and b for the F_p equation: F_p = A * exp(-b * R)
// where R is the perimeter insulation R-value in imperial units (ft²·°F·h/BTU)
let FpCoefficients = {
  [OuterWallConstruction.EightInchBlockWall]: { A: 0.68, b: 0.057 },
  [OuterWallConstruction.FourInchBlockWall]:  { A: 0.84, b: 0.100 },
  [OuterWallConstruction.MetalStudWall]:      { A: 1.20, b: 0.151 },
  [OuterWallConstruction.PouredConcreteWall]: { A: 2.12, b: 0.200 },
};

export class Floor extends InputComponent {
  init() {
    this.floorType = new Field({
      name: 'Floor Type',
      type: FieldType.Select,
      choices: makeOptions(FloorType),
      bold: true,
    })

    this.area = new Field({
      name: 'Area',
      type: FieldType.Area,
      requiresInput: true,
    })
    this.area.makeUpdater((field) => {
      // Neither of these two floor types use area input
      field.visible = !(this.floorType.value === FloorType.FloorAboveConditionedSpace ||
          this.floorType.value === FloorType.SlabOnGrade);
    })

    this.onGradeGroup = new FieldGroup([
      new Field({
        key: 'outerWall',
        name: 'Outer wall construction',
        type: FieldType.Select,
        choices: makeOptions(OuterWallConstruction)
      }),
      new Field({
        key: 'perimeterInsulationRValue',
        name: 'Perimeter Insulation R-value',
        type: FieldType.RValue,
        defaultValue: 0,
        allowMin: true,
        min: 0,
      }),
      new Field({
        key: 'exposedPerimeter',
        name: 'Exposed perimeter',
        type: FieldType.Length,
        min: 0,
        allowMin: true,
      })
    ])
    this.onGradeGroup.setVisibility(() => {
      return this.floorType.value == FloorType.SlabOnGrade;
    })

    this.raisedGroup = new FieldGroup([
      new Field({
        key: 'floorRValue',
        name: 'Floor R-value',
        type: FieldType.RValue,
        requiresInput: true,
      })
    ])
    this.raisedGroup.setVisibility(() => {
      return this.floorType.value == FloorType.FloorRaisedOffGround;
    });

    this.belowGradeGroup = new FieldGroup([
      new Field({
        key: 'depth',
        name: 'Depth',
        type: FieldType.Length,
        requiresInput: true,
      }),
      new Field({
        key: 'floorInsulationRValue',
        name: 'R-value of Floor Insulation',
        type: FieldType.RValue,
        requiresInput: true,
        allowMin: true,
      }),
      new Field({
        key: 'wallInsulationRValue',
        name: 'R-value of Wall Insulation',
        type: FieldType.RValue,
        requiresInput: true,
        allowMin: true,
      }),
      new Field({
        key: 'shortestWidth',
        name: 'Shortest Width of Basement',
        type: FieldType.Length,
        requiresInput: true,
      }),
      new Field({
        key: 'belowGradeWallArea',
        name: 'Below-grade wall area',
        type: FieldType.Area,
        requiresInput: true,
      }),
      new Field({
        key: 'tempVariationEntryType',
        name: 'Amplitude of ground surface temp variation',
        type: FieldType.Select,
        choices: makeOptions(ManualOrAutomatic, [ManualOrAutomatic.Automatic, ManualOrAutomatic.Manual]),
        bold: true,
      }),
      new Field({
        key: 'tempVariation',
        name: 'Amplitude of ground surface temp variation',
        type: FieldType.TempDiffF,
      }),
      new Field({
        key: 'avgGroundTempEntryType',
        name: 'Average ground temp',
        type: FieldType.Select,
        choices: makeOptions(ManualOrAutomatic, [ManualOrAutomatic.Automatic, ManualOrAutomatic.Manual]),
        bold:  true,
      }),
      new Field({
        key: 'avgGroundTemp',
        name: 'Average ground temp',
        type: FieldType.Temp,
      }),
    ])
    for (const fieldName of ['tempVariation', 'avgGroundTemp']) {
      let field = this.belowGradeGroup.getField(fieldName);
      field.data = {defaultValue: field.value, useDefault: true};
    }
    this.belowGradeGroup.setVisibility(() => {
      return this.floorType.value == FloorType.SlabBelowGrade;
    })
    this.belowGradeGroup.getField('tempVariation').makeUpdater((field) => {
      let locationData = gApp.proj().getLocationData();
      if (locationData.isLocationSet()) {
        let weatherData = locationData.getOutputs();
        let lookupResult = GroundSurfaceTempAmplitudes.lookupValue(weatherData.latitude, weatherData.longitude);
        if (lookupResult === 0) {
          lookupResult = (weatherData.cooling0p4PerDryBulb - weatherData.heating99p6PerDryBulb) / 5;
        }
        field.data.defaultValue = lookupResult;
        field.isOutput = field.data.useDefault;
        if (field.data.useDefault) field.value = lookupResult;
        field.clearPriorityErrorMsg();
      } else {
        field.data.defaultValue = 0;
        field.isOutput = field.data.useDefault;
        if (field.data.useDefault) field.value = 0;
        field.setPriorityErrorMsg("Must set the location.")
      }
    })
    this.belowGradeGroup.getField('avgGroundTemp').makeUpdater((field) => {
      let locationData = gApp.proj().getLocationData();
      if (locationData.isLocationSet()) {
        let defaultValue = locationData.getOutputs().avgAnnualTemp;
        field.data.defaultValue = defaultValue;
        field.isOutput = field.data.useDefault;
        if (field.data.useDefault) field.value = defaultValue;
        field.clearPriorityErrorMsg();
      } else {
        field.data.defaultValue = 0;
        field.isOutput = field.data.useDefault;
        if (field.data.useDefault) field.value = 0;
        field.setPriorityErrorMsg("Must set the location.")
      }
    })
    this.aboveCrawlSpaceGroup = new FieldGroup([
      new Field({
        key: 'floorRValue',
        name: 'Floor R-value',
        type: FieldType.RValue,
        requiresInput: true,
      }),
      new Field({
        key: 'winterTemp',
        name: 'Winter temperature of space',
        type: FieldType.Temperature,
        requiresInput: true,
      }),
      new Field({
        key: 'summerTemp',
        name: 'Summer temperature of space',
        type: FieldType.Temperature,
        requiresInput: true,
      }),
      // TODO - implement the temperature calculator
    ])
    this.aboveCrawlSpaceGroup.setVisibility(() => {
      return this.floorType.value == FloorType.FloorAboveCrawlSpace;
    })

    this.additionalCoveringOption = new Field({
      name: 'Entry type',
      type: FieldType.Select,
      choices: makeOptions(ManualOrSelectFromList),
      bold: true,
    })
    this.manualCovering = new Field({
      name: 'R-value',
      type: FieldType.RValue,
      defaultValue: 0,
      requiresInput: false,
      // For the covering, R-value=0 is actually valid.
      // Never a divide-by-zero issue b/c U=1.0/(R_floor+R_covering)
      allowMin: true,
    })
    this.listCovering = new Field({
      name: 'Material types',
      type: FieldType.Select,
      choices: Floor.getFloorCoveringOptions(),
    })
    this.updater.addWatchEffect('show-covering', () => {
      // TODO - this visibility logic is super gross but fine for now.
      let showCovering = this.hasOptionalFloorCovering();
      if (showCovering) {
        this.additionalCoveringOption.visible = true;
        if (this.additionalCoveringOption.value == ManualOrSelectFromList.Manual) {
          this.manualCovering.visible = true;
          this.listCovering.visible = false;
        } else {
          this.manualCovering.visible = false;
          this.listCovering.visible = true;
        }
      } else {
        this.additionalCoveringOption.visible = false;
        this.manualCovering.visible = false;
        this.listCovering.visible = false;
      }
    })

    this.heatingLoadHelpInfo = null;
    this.coolingLoadHelpInfo = null;
    this.updater.addWatchEffect('floor-calc-help', () => {
      // If there are input errors, don't try calculating yet (will just spam the
      // console with errors).
      // TODO - this seems to cause weird problems. Investigate later.
      /*
      let errorCollector = new ErrorCollector(this, {});
      let errors = errorCollector.getErrors();
      if (errors.length > 0) {
        this.heatingLoadHelpInfo = {
          helpText: "Please fill out all fields correctly to see load previews.",
        }
        this.coolingLoadHelpInfo = null;
        return;
      }
      */

      // Try calculating the preview loads.
      try {
        let ctx = CalcContext.create();
        if (gApp.proj().isResidential()) {
          gApp.proj().toplevelData.calcOutputs(ctx);
          gApp.proj().designTempInputs.calcOutputs(ctx);
        } else {
          let locationData = gApp.proj().buildingAndEnv.getLocationData();
          ctx.toplevelData = {
            locationData: locationData.getOutputs(),
          }
        }
        let sampleTemps = gApp.proj().getSampleTemps();
        let heatingResult = this._calcLoads(ctx, true, {
            t_i: sampleTemps.t_i_winter, t_o: sampleTemps.t_o_winter});
        let coolingResult = this._calcLoads(ctx, false, {
            t_i: sampleTemps.t_i_summer, t_o: sampleTemps.t_o_summer});
        this.heatingLoadHelpInfo = heatingResult.helpInfo;
        this.coolingLoadHelpInfo = coolingResult.helpInfo;
      } catch (err) {
        // Ignore exception - likely just fields need to be filled out.
        // TODO - at some point, we should only run this when all the inputs are ready.
        //console.log("Error calculating floor loads: ", err);
        this.heatingLoadHelpInfo = {
          helpText: "Please fill out all fields correctly to see load previews.",
        }
        this.coolingLoadHelpInfo = null;
      }
    })

    this.serFields = [
      'floorType',
      'area',
      'onGradeGroup',
      'raisedGroup',
      'belowGradeGroup',
      'aboveCrawlSpaceGroup', 
      'additionalCoveringOption',
      'manualCovering',
      'listCovering',
    ]
    this.childObjs = '$auto'
    this.objInfo = {
      _name: 'Floor',
      path: 'floor',
    }
  }

  postReadFromJson(jsonData, opts) {
    // Migration: old projects used a 'perimeterInsulation' select field (Insulated/Uninsulated).
    // Map to the new 'perimeterInsulationRValue' numeric field:
    //   Uninsulated -> R = 0
    //   Insulated   -> R = 5.4 (imperial h·ft²·°F/BTU, equivalent to 0.95 m²·°C/W)
    if (jsonData.onGradeGroup && jsonData.onGradeGroup.perimeterInsulation) {
      let oldData = jsonData.onGradeGroup.perimeterInsulation;
      // Handle both old ('value') and newer ('_value') serialization formats
      let oldValue = '_value' in oldData ? oldData._value : oldData.value;
      if (oldValue === 'Insulated') {
        this.onGradeGroup.getField('perimeterInsulationRValue').value = 5.4;
      }
      // 'Uninsulated' (or anything else) leaves the field at its default of 0
    }

    let belowGradeData = jsonData.belowGradeGroup;
    if (belowGradeData) {
      for (const [fieldName, entryTypeName] of [
        ['tempVariation', 'tempVariationEntryType'],
        ['avgGroundTemp', 'avgGroundTempEntryType'],
      ]) {
        let fieldData = belowGradeData[fieldName];
        if (fieldData?.data?.useDefault === undefined) {
          let legacyEntry = belowGradeData[entryTypeName];
          let legacyValue = legacyEntry?._value ?? legacyEntry?.value;
          let wasManual = legacyValue === ManualOrAutomatic.Manual;
          let field = this.belowGradeGroup.getField(fieldName);
          field.data.useDefault = !wasManual;
          field.data.defaultValue = field.value;
          field.isOutput = !wasManual;
          if (wasManual) {
            field.value = fieldData?._value ?? fieldData?.value ?? field.value;
          }
        }
      }
    }
  }

  getFloorType() {
    return this.floorType.value;
  }

  hasOptionalFloorCovering() {
    return this.floorType.value !== FloorType.FloorAboveConditionedSpace;
  }

  isFloorRaised() {
    return this.floorType.value == FloorType.FloorRaisedOffGround;
  }

  getArea() {
    return this.area.value;
  }

  getTypesSet() {
    // Floors have no dependent types for now, but implement this
    // func for compat with other types.
    let typesSet = TypesSet.create();
    return typesSet;
  }

  static getFloorCoveringOptions() {
    let options = []
    for (const coveringName in kFloorCoverings) {
      let valueStr = Quantity.makeValStr(kFloorCoverings[coveringName], Units.RValue, {
        includeUnits: false,
      });
      let option = {
        label: `${coveringName} (R-value=${valueStr})`,
        value: coveringName,
      }
      options.push(option);
    }
    return options;
  }

  getManualRValue() {
    if (this.floorType.value == FloorType.FloorAboveCrawlSpace) {
      return this.aboveCrawlSpaceGroup.getField('floorRValue').value;
    } else if (this.floorType.value == FloorType.FloorRaisedOffGround) {
      return this.raisedGroup.getField('floorRValue').value;
    }
    throw new Error(`Cannot get manual R-value for floor of type ${this.floorType.value}`);
  }

  getFloorCoveringRValue() {
    if (this.additionalCoveringOption.value == ManualOrSelectFromList.Manual) {
      return this.manualCovering.value;
    } else {
      let coveringName = this.listCovering.value;
      if (!(coveringName in kFloorCoverings)) {
        throw new Error(`Unknown floor covering: ${coveringName}`);
      }
      return kFloorCoverings[coveringName]
    }
  }

  getCrawlSpaceTemp(season) {
    if (this.floorType.value !== FloorType.FloorAboveCrawlSpace) {
      throw new Error(`Cannot get crawl space temp for floor type ${this.floorType.value}`);
    }
    return season == Season.Winter ?
      this.aboveCrawlSpaceGroup.getField('winterTemp').value :
      this.aboveCrawlSpaceGroup.getField('summerTemp').value;
  }

  _calcLoads(ctx, isHeating, optArgs) {
    ctx.startSection(`Floor loads - ${isHeating ? 'Heating' : 'Cooling'}`);

    let helpInfo = null;
    let weatherData = ctx.toplevelData.locationData;
    ctx.floorType = this.floorType.value;
    if (gApp.proj().isResidential()) {
      ctx.t_i = isHeating ? ctx.toplevelData.indoorWinterTemp
        : ctx.toplevelData.indoorSummerTemp;
      ctx.t_o = isHeating? ctx.designTemps.heating : ctx.designTemps.cooling;
    } else {
      // For commercial, t_i and t_o must be given in the optArgs
      ctx.log("For commercial projects, using t_i and t_o given in optArgs...")
      ctx.assert(optArgs, 'optArgs must be given for commercial projects');
      ctx.assert(optArgs.t_i !== undefined, 'optArgs.t_i must be given for commercial projects');
      ctx.assert(optArgs.t_o !== undefined, 'optArgs.t_o must be given for commercial projects');
      ctx.t_i = optArgs.t_i;
      ctx.t_o = optArgs.t_o;
    }

    ctx.A = this.area.value;
    ctx.R_cvr = this.getFloorCoveringRValue();
    if (this.floorType.value == FloorType.SlabOnGrade) {
      if (isHeating) {
        ctx.wallConstruction = this.onGradeGroup.getField('outerWall').value;
        // R is stored in imperial units (field.value is always imperial)
        ctx.R = this.onGradeGroup.getField('perimeterInsulationRValue').value;
        ctx.P = this.onGradeGroup.getField('exposedPerimeter').value;
        let fpCoeffs = FpCoefficients[ctx.wallConstruction];
        ctx.A_fp = fpCoeffs.A;
        ctx.b_fp = fpCoeffs.b;
        // F_p = A * exp(-b * R)
        ctx.F_p = ctx.eval('A_fp * exp(-b_fp * R)', {exp: Math.exp}, 'F_p');
        // TODO - should t_o be t_gr here?
        ctx.q = ctx.eval('F_p*(t_i - t_o)*P', {}, 'q')
        helpInfo = {
          helpText: `The heating load for a slab-on-grade floor is calculated as: `
                  + `<b>q = F_p*(t_i - t_o)*P</b>, where F_p is the perimeter factor, t_i is the indoor temperature, `
                  + `t_o is the outdoor temperature, and P is the exposed perimeter. `
                  + `F_p is calculated as <b>F_p = A * exp(-b * R)</b>, where A and b are wall-type coefficients `
                  + `and R is the perimeter insulation R-value.`,
          result: {
            label: 'Heating load',
            value: ctx.q,
            units: Units.Load,
          },
          relatedValues: {
            F_p: {
              label: 'Perimeter factor (F_p)',
              value: ctx.F_p,
              // Same units as Soil Conductivity
              units: Units.SoilConductivity,
            },
            R: {
              label: 'Perimeter insulation R-value',
              value: ctx.R,
              units: Units.RValue,
            },
            t_i: {
              label: 'Indoor temperature',
              value: ctx.t_i,
              units: Units.F,
            },
            t_o: {
              label: 'Outdoor temperature',
              value: ctx.t_o,
              units: Units.F,
            },
          }
        }
      } else {
        ctx.q = 0;
        helpInfo = {
          helpText: "Slab-on-grade floors have no cooling load.",
          result: {
            label: 'Cooling load',
            value: 0,
            units: Units.Load,
          }
        }
      }
    } else if (this.floorType.value == FloorType.SlabBelowGrade) {
      if (isHeating) {
        ctx.groundTempEntryType = this.belowGradeGroup.getField('avgGroundTempEntryType').value; 
        ctx.t_gr_avg = this.belowGradeGroup.getField('avgGroundTemp').value;
        ctx.A_gr = this.belowGradeGroup.getField('tempVariation').value;
        ctx.t_gr = ctx.eval('t_gr_avg - A_gr', {}, 't_gr');

        ctx.k_soil = weatherData.k_soil;
        ctx.d = this.belowGradeGroup.getField('depth').value;
        ctx.R_other = ctx.eval('R_wall_insul + 1.47', {
          R_wall_insul: this.belowGradeGroup.getField('wallInsulationRValue').value
        }, 'R_other');

        // Wall
        ctx.U_avg_bw = ctx.eval(
          '((2*k_soil)/(pi*d))*(ln(d + 2*k_soil*R_other/pi) - ln(2*k_soil*R_other/pi))',
          {pi: Math.PI, ln: Math.log,}, 'U_avg_bw')
        ctx.A_wall = this.belowGradeGroup.getField('belowGradeWallArea').value;
        ctx.q_bw = ctx.eval('U_avg_bw*(t_i - t_gr)*A_wall', {}, 'q_bw')

        // Floor
        ctx.w_b = this.belowGradeGroup.getField('shortestWidth').value;
        ctx.R_other = ctx.eval('R_floor_insul + R_cvr + 1.47', {
          R_floor_insul: this.belowGradeGroup.getField('floorInsulationRValue').value,
        }, 'R_other')
        ctx.U_avg_bf = ctx.eval(
          '(2*k_soil/(pi*w_b))*(ln(w_b/2 + d/2 + k_soil*R_other/pi) - ln(d/2 + k_soil*R_other/pi))',
          {pi: Math.PI, ln: Math.log,}, 'U_avg_bf')
        ctx.q_bf = ctx.eval('U_avg_bf*(t_i - t_gr)*A', {}, 'q_bf')

        ctx.q = ctx.q_bw + ctx.q_bf;
        
        helpInfo = {
          helpText: `The heating load is calculated as the sum of wall and floor heat transfer: `
                    + `<b>q = U_wall*(t_i - t_gr)*A_wall + U_floor*(t_i - t_gr)*A_floor</b>, `
                    + `where t_i is the indoor temp, t_gr is the ground temp, and U-values are calculated from the inputs `
                    + `and soil conductivity.`,
          result: {
            label: 'Heating load',
            value: ctx.q,
            units: Units.Load,
          },
          relatedValues: {
            q_bw: {
              label: 'Wall heat transfer',
              value: ctx.q_bw,
              units: Units.Load,
            },
            q_bf: {
              label: 'Floor heat transfer',
              value: ctx.q_bf,
              units: Units.Load,
            },
            t_gr: {
              label: 'Ground temperature',
              value: ctx.t_gr,
              units: Units.F,
            },
            t_i: {
              label: 'Indoor temperature',
              value: ctx.t_i,
              units: Units.F,
            },
            U_wall: {
              label: 'Wall U-value',
              value: ctx.U_avg_bw,
              units: Units.UValue,
            },
            A_wall: {
              label: 'Wall area',
              value: ctx.A_wall,
              units: Units.ft2,
            },
            U_floor: {
              label: 'Floor U-value',
              value: ctx.U_avg_bf,
              units: Units.UValue,
            },
            A_floor: {
              label: 'Floor area',
              value: ctx.A,
              units: Units.ft2,
            },
          }
        }
      } else {
        ctx.q = 0;
        helpInfo = {
          helpText: "Below-grade floors have no cooling load.",
          result: {
            label: 'Cooling load',
            value: 0,
            units: Units.Load,
          }
        }
      }
    } else if (this.floorType.value == FloorType.FloorAboveCrawlSpace) {
      // Treat as a partition
      ctx.t_b = isHeating ? this.aboveCrawlSpaceGroup.getField('winterTemp').value
        : this.aboveCrawlSpaceGroup.getField('summerTemp').value;
      ctx.R = ctx.eval('R_floor + R_cvr', {
        R_floor: this.aboveCrawlSpaceGroup.getField('floorRValue').value,
      }, 'R')
      ctx.U = 1.0 / ctx.R;
      if (isHeating) {
        // TODO - see Calculation note about if t_b > t_i
        ctx.q = ctx.eval('A*U*(t_i - t_b)', {
        }, 'q');
        helpInfo = {
          helpText: `Floor above crawl space heating load is calculated using the temperature difference between indoor and crawl space temperatures: `
                    + `<b>q = U*(t_i - t_b)*A</b>, where t_i is the indoor temperature, and t_b is the crawl space temperature.`,
          result: {
            label: 'Heating load',
            value: ctx.q,
            units: Units.Load,
          },
          relatedValues: {
            R: {
              label: 'Total R-value',
              value: ctx.R,
              units: Units.RValue,
            },
            U: {
              label: 'U-value',
              value: ctx.U,
              units: Units.UValue,
            },
            t_i: {
              label: 'Indoor temperature',
              value: ctx.t_i,
              units: Units.F,
            },
            t_b: {
              label: 'Crawl space temperature',
              value: ctx.t_b,
              units: Units.F,
            }
          }
        }
      } else {
        ctx.q = ctx.eval('A*U*(t_b - t_i)', {
        }, 'q');
        helpInfo = {
          helpText: `Floor above crawl space cooling load is calculated using the temperature difference between crawl space and indoor temperatures: `
                    + `<b>q = U*(t_b - t_i)*A</b>, where t_i is the indoor temperature, and t_b is the crawl space temperature.`,
          result: {
            label: 'Cooling load',
            value: ctx.q,
            units: Units.Load,
          },
          relatedValues: {
            R: {
              label: 'Total R-value',
              value: ctx.R,
              units: Units.RValue,
            },
            U: {
              label: 'U-value',
              value: ctx.U,
              units: Units.UValue,
            },
            t_i: {
              label: 'Indoor temperature',
              value: ctx.t_i,
              units: Units.F,
            },
            t_b: {
              label: 'Crawl space temperature',
              value: ctx.t_b,
              units: Units.F,
            }
          }
        }
      }
    } else if (this.floorType.value == FloorType.FloorAboveConditionedSpace) {
      // No heating/cooling load
      ctx.q = 0;
      helpInfo = {
        helpText: `Floors above conditioned spaces have no ${isHeating ? 'heating' : 'cooling'} load.`,
        result: {
          label: isHeating ? 'Heating load' : 'Cooling load',
          value: 0,
          units: Units.Load,
        }
      }
    } else if (this.floorType.value == FloorType.FloorRaisedOffGround) {
      // Treat as a wall
      ctx.R = ctx.eval('R_floor + R_cvr', {
        R_floor: this.raisedGroup.getField('floorRValue').value,
      }, 'R');
      ctx.U = 1.0 / ctx.R;
      let surfaceType = 'FloorRaisedOffGround';
      if (isHeating) {
        ctx.q = ctx.eval('A*U*(t_i - t_o)', {}, 'q');
        helpInfo = {
          helpText: `Raised floor heating load is calculated using the temperature difference between indoor and outdoor temperatures: `
                  + `<b>q = U*(t_i - t_o)*A</b>, where t_i is the indoor temperature, and t_o is the outdoor temperature.`,
          result: {
            label: 'Heating load',
            value: ctx.q,
            units: Units.Load,
          },
          relatedValues: {
            R: {
              label: 'Total R-value',
              value: ctx.R,
              units: Units.RValue,
            },
            U: {
              label: 'U-value',
              value: ctx.U,
              units: Units.UValue,
            },
            t_i: {
              label: 'Indoor temperature',
              value: ctx.t_i,
              units: Units.F,
            },
            t_o: {
              label: 'Outdoor temperature',
              value: ctx.t_o,
              units: Units.F,
            }
          }
        }
      } else {
        if (gApp.proj().isResidential()) {
          // Note: we keep these different for now because the residential app uses some adjustment factors
          // internally.
          // TODO - verify that those are actually needed / desired - would be better to standardize on one way.
          ctx.q = calc.calcOpaqueQ(ctx, ctx.A,
            ctx.U, ctx.t_i, ctx.t_o, null,
            isHeating, surfaceType, weatherData);
        } else {
          ctx.q = ctx.eval('A*U*(t_o - t_i)', {}, 'q');
        }
        helpInfo = {
          helpText: `Raised floor cooling load is calculated using the temperature difference between outdoor and indoor temperatures: `
                  + `<b>q = U*(t_o - t_i)*A</b>, where t_i is the indoor temperature, and t_o is the outdoor temperature. For houses, `
                  + `some adjustment factors are also applied.`,
          result: {
            label: 'Cooling load',
            value: ctx.q,
            units: Units.Load,
          },
          relatedValues: {
            R: {
              label: 'Total R-value',
              value: ctx.R,
              units: Units.RValue,
            },
            U: {
              label: 'U-value',
              value: ctx.U,
              units: Units.UValue,
            },
            t_i: {
              label: 'Indoor temperature',
              value: ctx.t_i,
              units: Units.F,
            },
            t_o: {
              label: 'Outdoor temperature',
              value: ctx.t_o,
              units: Units.F,
            }
          }
        }
      }
    } else {
      throw new Error("Unknown FloorType: " + this.floorType.value);
    }

    let q = ctx.q;
    ctx.endSection();
    return {
      q: q,
      helpInfo: helpInfo,
    };
  }

  calcOutputs(ctx) {
    let result = {};
    result.q_heating = this._calcLoads(ctx, true).q;
    result.q_cooling = this._calcLoads(ctx, false).q;
    return result;
  }
}
setupClass(Floor)
